/**
 * Helius Enhanced API Service for Znoospace
 * 
 * Replaces raw Solana RPC N+1 calls with Helius's optimized transaction API.
 * Free tier: 100K credits/day, more than enough for devnet/early mainnet.
 * 
 * Key improvements over raw RPC:
 * - Single API call returns parsed transactions (no N+1 getTransaction calls)
 * - Built-in rate limiting and retry logic
 * - Filter by transaction type (MEMO)
 * - Structured response with decoded memo data
 */

import bs58 from 'bs58';

const HELIUS_API_BASE = 'https://api-devnet.helius.xyz'; // devnet
// const HELIUS_API_BASE = 'https://api.helius.xyz'; // mainnet

class HeliusService {
  constructor(apiKey) {
    this.apiKey = apiKey;
    this.baseUrl = HELIUS_API_BASE;
    this.cache = new Map();
    this.cacheExpiry = 60 * 1000; // 1 minute cache
    this.maxRetries = 2;
    this.retryDelay = 1000;
  }

  /**
   * Check if Helius is configured and available
   */
  isAvailable() {
    return !!this.apiKey;
  }

  /**
   * Get parsed transaction history for an address.
   * Returns enriched transactions with decoded memo data in a single call.
   * Replaces: getSignaturesForAddress + N × getTransaction
   * 
   * @param {string} address - Wallet address to query
   * @param {object} options - Query options
   * @param {string} options.before - Pagination cursor (oldest tx signature from previous page)
   * @param {number} options.limit - Max transactions to return (default 50, max 100)
   * @param {string} options.type - Filter by transaction type (e.g., 'MEMO')
   * @returns {Array} Parsed transactions
   */
  async getTransactions(address, options = {}) {
    const { before, limit = 50, type } = options;

    // Build query params
    const params = new URLSearchParams({ 'api-key': this.apiKey });
    if (before) params.append('before', before);
    if (limit) params.append('limit', Math.min(limit, 100).toString());
    if (type) params.append('type', type);

    const url = `${this.baseUrl}/v0/addresses/${address}/transactions?${params}`;

    // Check cache
    const cacheKey = url;
    const cached = this._getCached(cacheKey);
    if (cached) return cached;

    const data = await this._fetch(url);
    // Only cache non-empty results to avoid caching Helius indexing delays
    if (Array.isArray(data) && data.length > 0) {
      this._setCache(cacheKey, data);
    }
    return data;
  }

  /**
   * Get Znoospace beams for a specific wallet.
   * Fetches memo transactions and parses out Znoospace beam data.
   * 
   * @param {string} walletAddress - Wallet to query
   * @param {object} options - { before, limit }
   * @returns {{ beams: Array, nextCursor: string|null, hasMore: boolean }}
   */
  async getBeamsForWallet(walletAddress, options = {}) {
    const { before, limit = 20 } = options;

    try {
      // Fetch more than needed since not all memo txns are Znoospace beams
      const fetchLimit = Math.min(limit * 3, 100);
      const transactions = await this.getTransactions(walletAddress, {
        before,
        limit: fetchLimit,
      });

      if (!Array.isArray(transactions)) {
        console.warn('⚠️ Helius returned non-array:', typeof transactions);
        return { beams: [], nextCursor: null, hasMore: false };
      }

      // Minimal logging — avoid dumping full transaction data to console
      if (transactions.length > 0) {
        console.log(`🔍 Helius returned ${transactions.length} transactions`);
      }

      const beams = [];
      for (const tx of transactions) {
        const beam = this._parseBeamFromHeliusTx(tx);
        if (beam) {
          beams.push(beam);
          if (beams.length >= limit) break;
        }
      }

      // Pagination cursor: last transaction signature
      const lastTx = transactions[transactions.length - 1];
      const nextCursor = transactions.length === fetchLimit ? lastTx?.signature : null;

      return {
        beams,
        nextCursor,
        hasMore: transactions.length === fetchLimit,
        totalFetched: transactions.length,
      };
    } catch (error) {
      console.error('❌ Helius getBeamsForWallet error:', error.message);
      return { beams: [], nextCursor: null, hasMore: false };
    }
  }

  /**
   * Get a global feed by querying multiple wallets.
   * Merges and sorts beams from all followed wallets + own wallet.
   * 
   * @param {string[]} walletAddresses - Array of wallet addresses to query
   * @param {object} options - { limit }
   * @returns {{ beams: Array, hasMore: boolean }}
   */
  async getGlobalFeed(walletAddresses, options = {}) {
    const { limit = 30 } = options;

    try {
      // Query all wallets in parallel (Helius handles rate limiting)
      const perWalletLimit = Math.max(10, Math.ceil(limit / walletAddresses.length));
      
      const results = await Promise.allSettled(
        walletAddresses.map(addr =>
          this.getBeamsForWallet(addr, { limit: perWalletLimit })
        )
      );

      // Merge all beams
      const allBeams = [];
      for (const result of results) {
        if (result.status === 'fulfilled' && result.value.beams) {
          allBeams.push(...result.value.beams);
        }
      }

      // Deduplicate by signature
      const seen = new Set();
      const unique = allBeams.filter(beam => {
        if (seen.has(beam.signature)) return false;
        seen.add(beam.signature);
        return true;
      });

      // Sort by timestamp descending (newest first)
      unique.sort((a, b) => b.timestamp - a.timestamp);

      return {
        beams: unique.slice(0, limit),
        hasMore: unique.length > limit,
      };
    } catch (error) {
      console.error('❌ Helius getGlobalFeed error:', error.message);
      return { beams: [], hasMore: false };
    }
  }

  /**
   * Parse a Znoospace beam from a Helius enhanced transaction object.
   * Helius returns structured data with decoded instructions.
   */
  _parseBeamFromHeliusTx(tx) {
    if (!tx || tx.transactionError) return null;

    try {
      // Helius enhanced format includes decoded instructions
      const instructions = tx.instructions || [];
      const innerInstructions = tx.innerInstructions || [];

      // Check all instructions for memo program
      const allInstructions = [
        ...instructions,
        ...innerInstructions.flatMap(ii => ii.instructions || []),
      ];

      for (const inst of allInstructions) {
        // Helius labels memo program instructions
        if (inst.programId === 'MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr') {
          const memoData = inst.data || '';
          
          // Try to parse Znoospace beam
          const beam = this._parseMemoContent(memoData, tx);
          if (beam) return beam;
        }
      }

      // Also check the description field (Helius sometimes puts memo content here)
      if (tx.description && tx.description.includes('Znoospace Beam')) {
        return this._parseMemoContent(tx.description, tx);
      }

      // Check events.nft or events.swap for memo data in some tx types
      // Fallback: check raw log messages if available
      if (tx.events?.memo) {
        return this._parseMemoContent(tx.events.memo, tx);
      }

      return null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Parse memo content string into a beam object
   */
  _parseMemoContent(memoContent, tx) {
    if (!memoContent) return null;

    let content = memoContent;
    
    // Helius returns memo instruction data as base58-encoded bytes
    // Try base58 decode first, then base64, then treat as plain text
    if (!content.includes('Znoospace') && !content.includes('ChirpX')) {
      // Try base58 decode (Helius default format for instruction data)
      try {
        const decoded = Buffer.from(bs58.decode(content)).toString('utf8');
        if (decoded.includes('Znoospace') || decoded.includes('ChirpX')) {
          content = decoded;
        }
      } catch {
        // Not base58
      }
      
      // Try base64 decode as fallback
      if (!content.includes('Znoospace') && !content.includes('ChirpX')) {
        try {
          const decoded = atob(content);
          if (decoded.includes('Znoospace') || decoded.includes('ChirpX')) {
            content = decoded;
          } else {
            return null; // Not a Znoospace memo
          }
        } catch {
          return null; // Not decodable and not Znoospace
        }
      }
    }

    // Skip non-beam memos (DMs, profiles)
    if (content.includes('Znoospace DM:') || content.includes('Znoospace Profile:') || content.includes('ChirpX Profile:')) {
      return null;
    }

    // Parse "Znoospace Beam: {...}" format
    const beamMatch = content.match(/(?:Znoospace|ChirpX) Beam: (.+)/);
    if (!beamMatch) return null;

    try {
      let jsonStr = beamMatch[1];
      if (jsonStr.startsWith('"') && jsonStr.endsWith('"')) jsonStr = jsonStr.slice(1, -1);
      jsonStr = jsonStr.replace(/\\"/g, '"');
      const beamData = JSON.parse(jsonStr);

      if (!beamData.content) return null;

      // Extract author from transaction
      const author = beamData.author || 
        tx.feePayer || 
        (tx.accountData?.[0]?.account) || 
        'Unknown';

      return {
        id: tx.signature,
        signature: tx.signature,
        content: `${beamData.content}\n\n🔗 View on Solana Explorer: https://explorer.solana.com/tx/${tx.signature}?cluster=devnet`,
        rawContent: beamData.content,
        author,
        username: `@${author.slice(0, 8)}...`,
        displayName: beamData.displayName || `User ${author.slice(0, 8)}`,
        timestamp: (tx.timestamp || 0) * 1000,
        likes: 0,
        reposts: 0,
        replies: 0,
        txId: tx.signature,
        type: 'znoospace_beam',
        fee: tx.fee,
        explorerUrl: `https://explorer.solana.com/tx/${tx.signature}?cluster=devnet`,
        hasMemo: true,
        mediaUrl: beamData.mediaUrl || null,
        source: 'helius',
      };
    } catch {
      return null;
    }
  }

  /**
   * Fetch with retry logic
   */
  async _fetch(url, retries = 0) {
    try {
      const response = await fetch(url);

      if (response.status === 429) {
        // Rate limited - wait and retry
        if (retries < this.maxRetries) {
          const delay = this.retryDelay * Math.pow(2, retries);
          console.warn(`⚠️ Helius rate limited, retrying in ${delay}ms...`);
          await new Promise(r => setTimeout(r, delay));
          return this._fetch(url, retries + 1);
        }
        throw new Error('Helius rate limit exceeded after retries');
      }

      if (!response.ok) {
        throw new Error(`Helius API error: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      if (retries < this.maxRetries && error.name !== 'AbortError') {
        const delay = this.retryDelay * Math.pow(2, retries);
        await new Promise(r => setTimeout(r, delay));
        return this._fetch(url, retries + 1);
      }
      throw error;
    }
  }

  /**
   * Cache management
   */
  _getCached(key) {
    const entry = this.cache.get(key);
    if (entry && Date.now() - entry.ts < this.cacheExpiry) {
      return entry.data;
    }
    if (entry) this.cache.delete(key);
    return null;
  }

  _setCache(key, data) {
    // Limit cache size
    if (this.cache.size > 100) {
      const oldest = [...this.cache.entries()]
        .sort((a, b) => a[1].ts - b[1].ts)
        .slice(0, 50);
      oldest.forEach(([k]) => this.cache.delete(k));
    }
    this.cache.set(key, { data, ts: Date.now() });
  }

  clearCache() {
    this.cache.clear();
  }
}

// Singleton instance
let heliusInstance = null;

export const getHeliusService = () => {
  if (!heliusInstance) {
    const apiKey = process.env.REACT_APP_HELIUS_API_KEY || '';
    heliusInstance = new HeliusService(apiKey);
    if (apiKey) {
      console.log('✅ Helius Enhanced API initialized');
    } else {
      console.warn('⚠️ No REACT_APP_HELIUS_API_KEY set - falling back to raw RPC');
    }
  }
  return heliusInstance;
};

export default HeliusService;
